import React, { useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { userAtom } from "../../store/store";
import { Link, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useUser } from "../../hooks/useUser";
import { googleClientId } from "../../constants/common";
import { useClickOutside } from "../../hooks/useClickOutside";
import Popover from "../../components/common/Popover/Popover";
import AuthModal from "../AuthModal/AuthModal";
import "./Header.css";

const Header: React.FC = () => {
  const location = useLocation();
  const user = useAtomValue(userAtom);
  const userAreaRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const { auth, openLoginModal, logout, closeAuthModal } = useUser();
  // 添加移动端导航状态
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavRef = useRef<HTMLDivElement>(null);

  useClickOutside(userAreaRef, () => {
    setShowDropdown(false);
  });

  // 添加移动端导航点击外部关闭
  useClickOutside(mobileNavRef, () => {
    setMobileNavOpen(false);
  });

  const handleShowDropdown = () => {
    setShowDropdown(true);
  };

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
  };

  // 切换移动端导航状态
  const toggleMobileNav = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMobileNavOpen((prev) => !prev);
  };

  // 点击导航链接后关闭移动端导航
  const handleNavLinkClick = () => {
    setMobileNavOpen(false);
  };

  // Create user feedback email link
  const getFeedbackMailto = () => {
    const subject = `User Feedback-${user.email || "Guest User"}`;
    const body = "Describe your issue";
    return `mailto:chinoname2@gmail.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <img className="img" src="logo512.png" alt="" />
              <span className="title">ChinoName</span>
            </div>
            {/* 桌面端导航 */}
            <nav className="nav-links desktop-nav">
              <Link
                to="/"
                className={`nav-link ${
                  location.pathname === "/" ? "active" : ""
                }`}
              >
                Home
              </Link>
              <Link
                to="/records"
                className={`nav-link ${
                  location.pathname === "/records" ? "active" : ""
                }`}
              >
                History
              </Link>
              <a
                href="https://name.chinolab.app/create-chinese-name"
                className={`nav-link`}
              >
                About
              </a>
            </nav>
            {/* 移动端汉堡菜单按钮 */}
            <div className="mobile-menu-toggle" onClick={toggleMobileNav}>
              <div className={`hamburger ${mobileNavOpen ? "open" : ""}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          <div className="header-right">
            {!user.isLogin ? (
              <div className="auth-buttons">
                <button className="btn-login" onClick={openLoginModal}>
                  Login
                </button>
              </div>
            ) : (
              <div className="user-area" ref={userAreaRef}>
                <Popover content={user.email} position="left">
                  <div className="avatar" onClick={handleShowDropdown}>
                    <span>{user.nickname}</span>
                  </div>
                </Popover>
                {showDropdown && (
                  <div className="dropdown-menu">
                    <a href={getFeedbackMailto()}>
                      <span>Feedback</span>
                    </a>
                    <span onClick={handleLogout}>Logout</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 移动端导航菜单 */}
        <div
          className={`mobile-nav ${mobileNavOpen ? "open" : ""}`}
          ref={mobileNavRef}
        >
          <nav className="mobile-nav-links">
            <Link
              to="/"
              className={`mobile-nav-link ${
                location.pathname === "/" ? "active" : ""
              }`}
              onClick={handleNavLinkClick}
            >
              Home
            </Link>
            <Link
              to="/records"
              className={`mobile-nav-link ${
                location.pathname === "/records" ? "active" : ""
              }`}
              onClick={handleNavLinkClick}
            >
              History
            </Link>
            <a
              href="https://name.chinolab.app/create-chinese-name"
              className={`mobile-nav-link`}
              onClick={handleNavLinkClick}
            >
              About
            </a>
          </nav>
        </div>
      </header>
      <GoogleOAuthProvider clientId={googleClientId}>
        <AuthModal
          isOpen={auth.modalVisible}
          onClose={closeAuthModal}
          initialMode={auth.mode}
        />
      </GoogleOAuthProvider>
    </>
  );
};

export default Header;
